<template>
  <div class="container">
    <v-card-title class="toolbar blue">
      <span class="headline white--text">Email Templates</span>
      <v-spacer />
      <v-text-field
        v-model="search"
        dark
        append-icon="search"
        label="Search"
        single-line
        hide-details
      />
    </v-card-title>

    <v-card-text class="white">
      <v-container>
        <v-layout class="btnWrapper">
          <v-btn @click="showModal('edit')">
            + Add Template
          </v-btn>
          <v-spacer />
        </v-layout>

        <v-data-table
          :headers="headers"
          :items="templates"
          :rows-per-page-items="rowsPerPageItems"
          :pagination.sync="pagination"
          :search="search"
          @update:pagination="updatePagination"
        >
          <template
            slot="items"
            slot-scope="props"
          >
            <td>{{ props.item.id }}</td>
            <td class="text-xs">
              {{ props.item.appClientId }}
            </td>
            <td class="text-xs">
              {{ props.item.name }}
            </td>
            <td class="text-xs">
              {{ props.item.sgTemplateId }}
            </td>
            <td class="text-xs">
              {{ props.item.language }}
            </td>
            <td class="text-xs">
              {{ props.item.dynamicKeys }}
            </td>
            <td class="text-xs-right">
              <v-btn
                color="blue"
                flat
                icon
                @click="showModal('edit', props.item.id)"
              >
                <v-icon>edit</v-icon>
              </v-btn>
              <v-btn
                color="blue"
                flat
                icon
                @click="showModal('delete', props.item.id)"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-container>
    </v-card-text>

    <v-dialog
      v-model="editModalOpen"
      width="400"
      persistent
    >
      <v-card>
        <v-form
          ref="templateForm"
          @submit.prevent="save"
        >
          <v-card-title
            class="headline grey lighten-2"
            primary-title
          >
            {{ mode === 'edit' ? 'Edit' : 'Create' }} Template Type
          </v-card-title>

          <v-card-text>
            <v-text-field
              v-model="activeTemplate.appClientId"
              label="Application Id"
              required
            />
            <v-autocomplete
              ref="templateTypeSelect"
              v-model="activeTemplate.typeId"
              :items="templateTypes"
              item-text="name"
              item-value="id"
              :filter="templateTypeFilter"
              label="Template Type"
            />
            <v-select
              ref="selectEnv"
              v-model="activeTemplate.language"
              :items="languages"
              label="Language"
            />
            <v-text-field
              v-model="activeTemplate.sgTemplateId"
              label="Sendgrid Template Id"
              required
            />
            <v-text-field
              v-model="activeTemplate.sgApiKey"
              label="Sendgrid API Key"
              required
            />
            <v-text-field
              v-model="activeTemplate.description"
              label="Description"
              required
            />
            <v-text-field
              v-model="activeTemplate.dynamicKeys"
              label="Dynamic Keys"
              required
            />
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn
              flat
              @click="showModal('edit', null, false)"
            >
              Close
            </v-btn>
            <e-button
              flat
              type="submit"
              color="blue"
              :text="mode === 'edit' ? 'Edit' : 'Create'"
              :loading="loading"
            />
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteModalOpen"
      width="400"
      persistent
    >
      <v-card>
        <v-card-title
          class="headline grey lighten-2"
          primary-title
        >
          Delete Template Type
        </v-card-title>

        <v-card-text>
          Do you want to delete {{ activeTemplate.type }} Template Type?
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            flat
            @click="showModal('delete', null, false)"
          >
            Close
          </v-btn>
          <e-button
            flat
            color="blue"
            :text="'Delete'"
            :loading="loading"
            @click="deleteActiveItem"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import _ from 'lodash';
import { Button } from '@/components/shared';
import { formRules, setQuery } from '@/helpers';

export default {
  name: 'Templates',

  components: {
    'e-button': Button,
  },

  data() {
    return {
      mode: 'create',
      editModalOpen: false,
      deleteModalOpen: false,
      isLoadTableData: false,
      activeId: null,
      activeTemplate: {},
      languages: window.LANGUAGES,
      search: '',
      headers: [
        { text: 'ID', value: 'id', sortable: true },
        { text: 'Application ID', value: 'appClientId', sortable: true },
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Sendgrid Template', value: 'sgTemplateId' },
        { text: 'Language', value: 'language' },
        { text: 'Dynamic Keys', value: 'dynamicKeys' },
        { text: 'Actions', value: 'actions' },
      ],
      rowsPerPageItems: [
        { text: '5', value: 5 },
        { text: '10', value: 10 },
        { text: '25', value: 25 },
        { text: 'All', value: -1 },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 5,
      },
    };
  },

  computed: {
    ...mapState({
      templates: state => state.templateStore.templates,
      templateTypes: state => state.templateTypeStore.templateTypes,
      loading: state => state.templateStore.loading,
    }),
  },

  watch: {
    loading(newVal, oldVal) {
      if (!newVal && oldVal) {
        this.editModalOpen = false;
        this.deleteModalOpen = false;
      }
    },
    pagination: {
      handler() {
        this.isLoadTableData = true;
      },
    },
  },

  async mounted() {
    this.getTemplates();
    this.getTemplateTypes();
    await this.$nextTick();
    this.changePage();
  },

  methods: {
    ...mapActions([
      'getTemplates',
      'createTemplate',
      'updateTemplate',
      'deleteTemplate',
      'getTemplateTypes',
    ]),

    formRules,

    save() {
      const isValid = this.$refs.templateForm.validate();

      if (isValid) {
        const validFields = [
          'id', 'appClientId', 'typeId', 'sgTemplateId',
          'sgApiKey', 'description', 'language', 'dynamicKeys',
        ];
        const payload = _.pick(this.activeTemplate, validFields);

        if (this.mode === 'create') {
          this.createTemplate(payload);
        }
        if (this.mode === 'edit') {
          this.updateTemplate(payload);
        }
      }
    },

    showModal(type, id, open = true) {
      if (type !== 'delete') {
        this.activeId = id;
        this.mode = 'create';
        this[`${type}ModalOpen`] = open;
        this.activeTemplate = _.find(this.templates, { id }) || {};

        if (id) {
          this.mode = 'edit';
        }
      } else {
        this.activeId = id;
        this.activeTemplate = {};
        this[`${type}ModalOpen`] = open;
      }
    },

    deleteActiveItem() {
      this.deleteTemplate(this.activeId);
      this.activeId = null;
    },
    updatePagination(pagination) {
      if (this.isLoadTableData) {
        setQuery({ page: pagination.page }, this.$route, this.$router);
      }
    },
    changePage() {
      const { page } = this.$route.query;
      this.pagination.page = page ? Number(page) : 1;
    },
    templateTypeFilter(item, queryText) {
      return item.name.indexOf(queryText) !== -1;
    },
  },
};
</script>

<style lang="scss">
.justfifyEnd {
  display: flex;
  justify-content: flex-end;
}
.btnWrapper {
  justify-content: flex-end;
  display: flex;
  flex-direction: row-reverse;
}
.noneDecoration {
  text-decoration: none;
}
</style>



// WEBPACK FOOTER //
// src/views/templates/Templates.vue